import { findLast } from 'lodash';

import { AnalyticsPeriodService } from '@/utils/services/analytics/AnalyticsPeriodService';
import { TimeService } from '@/utils/services/TimeService';
import type { DatepickerGlobalStoreStateType } from '@/utils/zustand/stores/datepicker-global-store';
import type { ProfilesStoreStateType } from '@/utils/zustand/stores/profiles-store';

import type { SpGroupType } from '@/utils/hooks/swr/group/useSPGroups';
import type { GlobalAiQuota } from '@/utils/hooks/swr/quotas/useGlobalAiQuota';

import type { SocialProfileType } from '@/types/socialProfileType';
import type {
    AiAnalyticsContextData,
    AiContext,
    AiMessage,
    AiMessagesListResponse,
    AiModelMergedProfilesContextData,
    AiPeriodWindowData,
    AiPostMessageResponse,
    AiToolResult,
    SparksMessage,
    SparksProactiveQuestion,
    SparksSocialProfilesContextStrategy,
    SparksSocialProfileSnapshot
} from '@/components/composite/SparksAgent/types';

import type { DatepickerPeriodsAvailableType } from '@/lib/periods';
import {
    ANALYZE_OVERVIEW,
    CONTENT_ALBUMS,
    CONTENT_POSTS,
    DASHBOARD,
    LISTENING_HASHTAGS,
    REPORTS,
    SCHEDULER,
    SOCIAL_PROFILES
} from '@/lib/routes/privateRoutes';
import {
    AI_CONVERSATION_MESSAGES_PAGE_SIZE,
    OPTIMISTIC_ID_PREFIX,
    QUESTION_POOL,
    SPARKS_GLOBAL_SOCIAL_SELECTION_ROUTE_PREFIXES
} from '@/components/composite/SparksAgent/const';

class SparksAgentService {
    // ------------------------
    // Public helpers

    getQuickQuestions = (): SparksProactiveQuestion[] => {
        const corePeriodQuestions = QUESTION_POOL.filter(
            (question) => question.intent === 'core_period_comparison'
        );
        const otherQuestions = QUESTION_POOL.filter(
            (question) => question.intent !== 'core_period_comparison'
        );

        const selectedCore =
            corePeriodQuestions[Math.floor(Math.random() * corePeriodQuestions.length)];
        const shuffledOtherQuestions = [...otherQuestions]
            .sort(() => Math.random() - 0.5)
            .slice(0, 2);

        return [selectedCore, ...shuffledOtherQuestions].sort(() => Math.random() - 0.5);
    };

    getGlobalAiQuotaDerived(aiQuota: GlobalAiQuota | undefined, isQuotaLoading: boolean) {
        const hasNumericQuota =
            aiQuota != null && aiQuota.total !== null && aiQuota.remaining !== null;

        const isLimitedQuota = Boolean(hasNumericQuota && !isQuotaLoading);
        const isQuotaExhausted = Boolean(isLimitedQuota && aiQuota!.remaining === 0);
        const showRemainingQuota = Boolean(
            isLimitedQuota && aiQuota!.remaining > 0 && aiQuota!.remaining < aiQuota!.total / 2
        );

        return { isLimitedQuota, isQuotaExhausted, showRemainingQuota };
    }

    formatCompactAiAmount(value: number, locale: string): string {
        return new Intl.NumberFormat(locale, {
            notation: 'compact',
            maximumFractionDigits: 1
        }).format(value);
    }

    /** Display name for Prism / Laravel Ai tool identifiers (e.g. `SocialProfileLister`). */
    getToolName(name: string): string {
        if (!name) return '';
        return name
            .replace(/([a-z\d])([A-Z])/g, '$1 $2')
            .replace(/[_-]+/g, ' ')
            .trim();
    }

    getAggregatedSourceContext(tools: AiToolResult[]): {
        networks?: string;
        periodComparison?: string;
        metrics?: string;
    } {
        const networkSet = new Set<string>();
        const metricSet = new Set<string>();
        let periodComparison: string | undefined;

        for (const tool of tools) {
            for (const toolResultOrArguments of [tool.result, tool.arguments]) {
                const record = this.asRecord(toolResultOrArguments);
                if (!record) continue;

                const networkStrings = this.readTrimmedStringList(
                    record.networks ?? record.network
                );
                networkStrings.forEach((networkName) => networkSet.add(networkName));

                const metricStrings = this.readTrimmedStringList(
                    record.metrics ?? record.metric_list ?? record.metricList
                );
                metricStrings.forEach((metricName) => metricSet.add(metricName));

                const periodComparisonRaw = this.readTrimmedString(
                    record.period_comparison ??
                    record.periodComparison ??
                    record.period ??
                    record.date_range
                );
                if (periodComparisonRaw && !periodComparison) {
                    periodComparison = periodComparisonRaw;
                }
            }
        }

        const networks = networkSet.size > 0 ? Array.from(networkSet).sort().join(', ') : undefined;
        const metrics = metricSet.size > 0 ? Array.from(metricSet).sort().join(', ') : undefined;

        return {
            ...(networks ? { networks } : {}),
            ...(periodComparison ? { periodComparison } : {}),
            ...(metrics ? { metrics } : {})
        };
    }

    getSourceChips(tools: AiToolResult[]): { key: string; label: string; href: string }[] {
        const fromPayload: { label: string; href?: string }[] = [];

        for (const tool of tools) {
            for (const toolResultOrArguments of [tool.result, tool.arguments]) {
                const record = this.asRecord(toolResultOrArguments);
                if (!record) continue;
                const navigationEntries = this.parseNavigationEntries(record);
                fromPayload.push(...navigationEntries);
            }
        }

        if (fromPayload.length > 0) {
            const seenDedupeKeys = new Set<string>();
            const chips: { key: string; label: string; href: string }[] = [];
            fromPayload.forEach((entry, index) => {
                const deduplicationKey = `${entry.label.toLowerCase()}|${entry.href ?? ''}`;
                if (seenDedupeKeys.has(deduplicationKey)) return;
                seenDedupeKeys.add(deduplicationKey);
                chips.push({
                    key: `nav-${index}-${deduplicationKey}`,
                    label: entry.label,
                    href: entry.href ?? this.getSparksToolNavigationHref(entry.label)
                });
            });
            return chips;
        }

        return tools.map((tool, index) => ({
            key: tool.id || `tool-${index}`,
            label: this.getToolName(tool.name),
            href:
                this.findFirstNavigableString(tool.result) ??
                this.findFirstNavigableString(tool.arguments) ??
                this.getSparksToolNavigationHref(tool.name)
        }));
    }

    private parseNavigationEntries(
        record: Record<string, unknown>
    ): { label: string; href?: string }[] {
        const navigationItems = record.links ?? record.navigation ?? record.chips;
        if (!Array.isArray(navigationItems)) {
            return [];
        }

        const entries: { label: string; href?: string }[] = [];

        for (const navigationItem of navigationItems) {
            const linkRecord = this.asRecord(navigationItem);
            if (!linkRecord) continue;
            const label = this.readTrimmedString(
                linkRecord.label ?? linkRecord.name ?? linkRecord.title
            );
            if (!label) continue;
            const href = this.readTrimmedString(
                linkRecord.href ?? linkRecord.url ?? linkRecord.path
            );
            entries.push({
                label,
                ...(href ? { href } : {})
            });
        }

        return entries;
    }

    private findFirstNavigableString(value: unknown): string | undefined {
        if (value === null || value === undefined) {
            return undefined;
        }
        if (typeof value === 'string') {
            const trimmed = value.trim();
            if (/^https?:\/\//i.test(trimmed)) {
                return trimmed;
            }
            if (trimmed.startsWith('/') && trimmed.length > 1) {
                return trimmed;
            }
            return undefined;
        }
        if (typeof value !== 'object') {
            return undefined;
        }
        if (Array.isArray(value)) {
            for (const arrayElement of value) {
                const found = this.findFirstNavigableString(arrayElement);
                if (found) {
                    return found;
                }
            }
            return undefined;
        }
        for (const nestedValue of Object.values(value)) {
            const found = this.findFirstNavigableString(nestedValue);
            if (found) {
                return found;
            }
        }
        return undefined;
    }

    /**
     * In-app path for a Sparks AI tool id (e.g. `SocialProfileLister`) when the API does not send
     * explicit `href` / `links` in the tool payload.
     */
    private getSparksToolNavigationHref(toolName: string): string {
        const normalizedToolName = toolName.replace(/[\s_-]+/g, '').toLowerCase();
        if (!normalizedToolName) {
            return ANALYZE_OVERVIEW;
        }

        if (
            normalizedToolName.includes('socialprofile') ||
            normalizedToolName.includes('profilelister')
        ) {
            return SOCIAL_PROFILES;
        }
        if (normalizedToolName.includes('campaign') || normalizedToolName.includes('album')) {
            return CONTENT_ALBUMS;
        }
        if (normalizedToolName.includes('content') && normalizedToolName.includes('post')) {
            return CONTENT_POSTS;
        }
        if (normalizedToolName.includes('report')) {
            return REPORTS;
        }
        if (normalizedToolName.includes('listening')) {
            return LISTENING_HASHTAGS;
        }
        if (
            normalizedToolName.includes('scheduler') ||
            normalizedToolName.includes('publish') ||
            normalizedToolName.includes('publishing')
        ) {
            return SCHEDULER;
        }
        if (normalizedToolName.includes('dashboard')) {
            return DASHBOARD;
        }
        if (
            normalizedToolName.includes('analytics') ||
            normalizedToolName.includes('metric') ||
            normalizedToolName.includes('kpi') ||
            normalizedToolName.includes('provider')
        ) {
            return ANALYZE_OVERVIEW;
        }

        return ANALYZE_OVERVIEW;
    }

    // ------------------------

    isFenceCodeClass(className?: string): boolean {
        return !!className && /(^|\s)language-[\w-]+/.test(className);
    }

    getAiMessageText(message: AiMessage): string {
        return message.role === 'assistant'
            ? message.output ?? message.content ?? ''
            : message.input ?? message.content ?? '';
    }

    toSparksMessage(message: AiMessage): SparksMessage {
        return {
            id: message.id,
            role: message.role === 'assistant' ? 'agent' : 'user',
            content: this.getAiMessageText(message),
            tool_results: message.tool_results,
            created_at: message.created_at,
            langfuse_trace_id: message.langfuse_trace_id ?? undefined,
        };
    }

    parseSendMessageResponse(rawData: AiPostMessageResponse): AiMessage {
        const input = this.toNullableString(rawData.input);
        const output = this.toNullableString(rawData.output);

        return {
            // POST /messages does not currently return a persisted message id, so
            // we generate a stable client-side key until the next revalidation.
            id: `ai-res-${Date.now()}`,
            role: 'assistant',
            input: input ?? undefined,
            output: output ?? undefined,
            context: this.asArray(rawData.context),
            tool_results: this.asArray(rawData.tool_results),
            created_at: new Date().toISOString()
        };
    }

    findNewestPendingOptimisticUserMessage(messages: AiMessage[]): AiMessage | undefined {
        return findLast(
            messages,
            (message) => message.role === 'user' && message.id.startsWith(OPTIMISTIC_ID_PREFIX)
        );
    }

    createOptimisticUserMessage(text: string): AiMessage {
        return {
            id: `${OPTIMISTIC_ID_PREFIX}${Date.now()}`,
            role: 'user',
            input: text,
            created_at: new Date().toISOString()
        };
    }

    /**
     * Dedupes `fields` from dashboard `widgetsParams` (same shape as `StatsDashboardWrapper` / analytics API).
     */
    collectUniqueDashboardWidgetFieldIds(
        widgetsParams: { fields: string[] }[] | undefined
    ): string[] {
        if (!widgetsParams?.length) return [];
        return widgetsParams.reduce<string[]>((acc, obj) => {
            obj.fields.forEach((field) => {
                if (!acc.includes(field)) acc.push(field);
            });
            return acc;
        }, []);
    }

    /**
     * Whether Sparks should send social profiles in context: omit (e.g. settings), global header selection, or dashboard-bound profiles.
     */
    resolveSparksSocialProfilesContextStrategy(
        pathname: string
    ): SparksSocialProfilesContextStrategy {
        const path = pathname || '';
        if (path.startsWith('/dashboard/view')) {
            return 'dashboard';
        }
        for (const prefix of SPARKS_GLOBAL_SOCIAL_SELECTION_ROUTE_PREFIXES) {
            if (path === prefix || path.startsWith(`${prefix}/`)) {
                return 'global';
            }
        }
        return 'omit';
    }

    /**
     * Social profiles attached to a custom dashboard (`dashboard.data.social_profiles` + members of `dashboard.data.groups`).
     */
    buildDashboardSparksSocialProfilesForContext(args: {
        socialProfileIds: SocialProfileType['id'][];
        groupIds: SpGroupType['id'][];
        activatedSocialProfiles: SocialProfileType[];
        activatedSpGroups: SpGroupType[];
    }): SparksSocialProfileSnapshot[] {
        const { socialProfileIds, groupIds, activatedSocialProfiles, activatedSpGroups } = args;

        const byId = (id: SocialProfileType['id']) =>
            activatedSocialProfiles.find((p) => p.id === id);

        const snapshots: SparksSocialProfileSnapshot[] = [];

        for (const id of socialProfileIds ?? []) {
            const s = this.toSocialProfileSnapshot(byId(id));
            if (s) snapshots.push(s);
        }

        for (const gid of groupIds ?? []) {
            const group = activatedSpGroups.find((g) => g.id === gid);
            if (!group?.social_profiles?.length) continue;
            for (const p of group.social_profiles.filter((sp) => sp.active)) {
                const s = this.toSocialProfileSnapshot(p);
                if (s) snapshots.push(s);
            }
        }

        return this.mergeSocialProfileSnapshotsForAiContext(snapshots);
    }

    /**
     * Context for POST /ai/conversations/{id}/messages (Laravel SendMessageRequest):
     *
     * @param analyticsFields - When `null` or `[]`, no `analytics` context item is sent. Otherwise includes `analytics` with those field ids.
     * @param socialProfiles - Only non-empty when strategy is `global` or `dashboard` (see `resolveSparksSocialProfilesContextStrategy`).
     */
    buildSparksAgentReadyContext(args: {
        datepicker: Pick<DatepickerGlobalStoreStateType, 'date' | 'period' | 'compareTo'>;
        socialProfiles: SparksSocialProfileSnapshot[];
        analyticsFields: string[] | null;
        /**
         * When false, no `period` context is added (e.g. global datepicker disabled on this route).
         * Defaults to true for callers that only need date + model/analytics.
         */
        includePeriodContext?: boolean;
    }): AiContext[] {
        const { datepicker, socialProfiles, analyticsFields, includePeriodContext = true } = args;

        const fields: string[] = analyticsFields != null ? [...analyticsFields] : [];

        const start = TimeService.ensureAPIDateFormat(datepicker.date.from);
        const end = TimeService.ensureAPIDateFormat(datepicker.date.to);

        const contexts: AiContext[] = [];

        if (includePeriodContext && start && end) {
            const { startComparison, endComparison } =
                AnalyticsPeriodService.calculateComparisonPeriod({
                    start,
                    end,
                    compareTo: datepicker.compareTo,
                    period: datepicker.period as DatepickerPeriodsAvailableType
                });

            contexts.push(
                {
                    type: 'period',
                    sub_type: 'current',
                    data: { start, end } satisfies AiPeriodWindowData
                },
                {
                    type: 'period',
                    sub_type: 'dashboard',
                    data: {
                        start: startComparison,
                        end: endComparison,
                        comparison_to_current: true
                    }
                }
            );
        }

        if (fields.length > 0) {
            contexts.push({
                type: 'analytics',
                sub_type: 'dashboard',
                data: { fields } satisfies AiAnalyticsContextData
            });
        }

        const mergedProfiles = this.mergeSocialProfileSnapshotsForAiContext(socialProfiles);
        if (mergedProfiles.length > 0) {
            contexts.push({
                type: 'model',
                sub_type: 'dashboard',
                data: { social_profiles: mergedProfiles } satisfies AiModelMergedProfilesContextData
            });
        }

        return contexts;
    }

    /**
     * Resolves which social profiles are in scope for analytics / Sparks context from header selection:
     * - Multi-selection → those profile ids.
     * - Group + a focused social profile → that profile only.
     * - Group + no focused profile → all active members of the group.
     * - Otherwise single active profile (no group) when set.
     */
    resolveAnalyticsScopeSocialProfiles(args: {
        activatedSocialProfiles: SocialProfileType[];
        activeSocialProfile: SocialProfileType | null | undefined;
        profilesStore: Pick<
            ProfilesStoreStateType,
            'activeSocialProfileId' | 'activeGroupId' | 'activeMultiSelectionProfiles'
        >;
        activeGroup: SpGroupType | null | undefined;
    }): SparksSocialProfileSnapshot[] {
        const { activatedSocialProfiles, activeSocialProfile, profilesStore, activeGroup } = args;

        const byId = (id: SocialProfileType['id']) =>
            activatedSocialProfiles.find((p) => p.id === id);

        if (profilesStore.activeMultiSelectionProfiles?.socialProfileIds?.length) {
            return profilesStore.activeMultiSelectionProfiles.socialProfileIds
                .map((id) => this.toSocialProfileSnapshot(byId(id)))
                .filter((s): s is SparksSocialProfileSnapshot => s !== null);
        }

        if (profilesStore.activeGroupId && activeGroup?.social_profiles?.length) {
            if (profilesStore.activeSocialProfileId && activeSocialProfile) {
                const one = this.toSocialProfileSnapshot(activeSocialProfile);
                return one ? [one] : [];
            }

            return activeGroup.social_profiles
                .filter((p) => p.active)
                .map((p) => this.toSocialProfileSnapshot(byId(p.id) ?? p))
                .filter((s): s is SparksSocialProfileSnapshot => s !== null);
        }

        if (profilesStore.activeSocialProfileId && activeSocialProfile) {
            const one = this.toSocialProfileSnapshot(activeSocialProfile);
            return one ? [one] : [];
        }

        return [];
    }

    appendOptimisticToInfiniteData({
        pages,
        message,
        perPage
    }: {
        pages: AiMessagesListResponse[] | undefined;
        message: AiMessage;
        perPage: number;
    }): AiMessagesListResponse[] {
        if (!pages?.length) {
            return [this.createPage([message], perPage)];
        }

        const [firstPage, ...remainingPages] = pages;
        return [this.prependToPage(firstPage, message), ...remainingPages];
    }

    appendAIOutputToInfiniteData({
        pages,
        serverMessage
    }: {
        pages: AiMessagesListResponse[] | undefined;
        serverMessage: AiMessage;
    }): AiMessagesListResponse[] {
        if (!pages?.length) {
            return [this.createPage([serverMessage], AI_CONVERSATION_MESSAGES_PAGE_SIZE)];
        }

        const [firstPage, ...remainingPages] = pages;

        return [
            {
                ...firstPage,
                data: [serverMessage, ...firstPage.data]
            },
            ...remainingPages
        ];
    }

    // ------------------------
    // SWR page data helpers
    // ------------------------

    private createPage(messages: AiMessage[], perPage: number): AiMessagesListResponse {
        return {
            ...this.emptyMessagesPage(perPage),
            data: messages
        };
    }

    private prependToPage(
        page: AiMessagesListResponse,
        message: AiMessage
    ): AiMessagesListResponse {
        return {
            ...page,
            data: [message, ...(page.data ?? [])]
        };
    }

    private emptyMessagesPage(perPage: number): AiMessagesListResponse {
        return {
            data: [],
            next_cursor: null,
            prev_cursor: null,
            next_page_url: null,
            prev_page_url: null,
            path: '',
            per_page: perPage
        };
    }

    // ------------------------
    // Generic utils
    // ------------------------

    private mergeSocialProfileSnapshotsForAiContext(
        profiles: SparksSocialProfileSnapshot[]
    ): SparksSocialProfileSnapshot[] {
        const byId = new Map<SocialProfileType['id'], SparksSocialProfileSnapshot>();

        for (const p of profiles) {
            if (byId.has(p.id)) continue;
            byId.set(p.id, {
                id: p.id,
                slug: p.slug,
                display_name: p.display_name,
                social_network_type: p.social_network_type
            });
        }

        return [...byId.values()];
    }

    private toSocialProfileSnapshot(
        profile: SocialProfileType | undefined
    ): SparksSocialProfileSnapshot | null {
        if (!profile) return null;

        return {
            id: profile.id,
            social_network_type: profile.social_network_type,
            slug: profile.slug,
            display_name:
                profile.external_api_data?.brand_name ||
                profile.external_api_data?.name ||
                profile.slug
        };
    }

    private toNullableString(value: unknown): string | null {
        return value != null ? String(value) : null;
    }

    private asArray<T>(value: unknown): T[] | undefined {
        return Array.isArray(value) ? (value as T[]) : undefined;
    }

    private asRecord(value: unknown): Record<string, unknown> | null {
        if (!value || typeof value !== 'object' || Array.isArray(value)) {
            return null;
        }
        return value as Record<string, unknown>;
    }

    private readTrimmedString(value: unknown): string | undefined {
        if (typeof value === 'string' && value.trim()) {
            return value.trim();
        }
        return undefined;
    }

    private readTrimmedStringList(value: unknown): string[] {
        if (Array.isArray(value)) {
            return value
                .filter(
                    (element): element is string =>
                        typeof element === 'string' && Boolean(element.trim())
                )
                .map((element) => element.trim());
        }
        const singleString = this.readTrimmedString(value);
        return singleString ? [singleString] : [];
    }
}

const sparksAgentService = new SparksAgentService();

export { sparksAgentService as SparksAgentService };
